import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
  Table as BsTable,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import type {
  LimitOffsetPaginationResponse,
  SerializedObject,
} from "../../../api/src/lib";

// Type definitions for risk asset class matrix
type FirmRiskProfile = {
  firmRiskId: number;
  firmId: number;
  name: string;
  description?: string;
  displayOrder: number;
  createdTime: Date;
  updatedTime: Date;
};

type AssetClassInfo = { name: string; displayOrder: number };

type RiskAssetClassMatrix = {
  assetClasses: AssetClassInfo[];
  riskProfiles: FirmRiskProfile[];
  allocations: Record<string, Record<string, number>>;
};
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import TabContainerWithTabs from "../components/TabContainer";
import {
  deserializeDate,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import FirmNav from "./FirmNav";

type AllocationChange = {
  firmRiskId: number;
  assetClass: string;
  allocationPercent: number;
};

const FirmRiskAssetClass = () => {
  const roles = useRoles();
  const notificationContext = useContext(NotificationContext);
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  // Local state for edited allocations
  const [allocations, setAllocations] = useState<
    Record<string, Record<string, number>>
  >({});
  const [originalAllocations, setOriginalAllocations] = useState<
    Record<string, Record<string, number>>
  >({});
  const [changedAllocations, setChangedAllocations] = useState<
    AllocationChange[]
  >([]);
  const [newAssetClassName, setNewAssetClassName] = useState("");
  const [localAssetClasses, setLocalAssetClasses] = useState<AssetClassInfo[]>(
    [],
  );
  const [deletedAssetClasses, setDeletedAssetClasses] = useState<string[]>([]);

  // Fetch the matrix data
  const {
    isPending,
    isError,
    data: matrixData,
  } = useAuthenticatedFetch<{
    data: SerializedObject<RiskAssetClassMatrix>;
  }>("/v1/firm/risk/asset-class");

  const matrix = useMemo(() => {
    if (!matrixData?.data) return null;
    return {
      ...matrixData.data,
      riskProfiles: matrixData.data.riskProfiles.map(
        (profile: SerializedObject<FirmRiskProfile>) => ({
          ...profile,
          createdTime: deserializeDate(profile.createdTime),
          updatedTime: deserializeDate(profile.updatedTime),
        }),
      ),
    } as RiskAssetClassMatrix;
  }, [matrixData?.data]);

  // Initialize local state when data loads
  useEffect(() => {
    if (matrix) {
      setAllocations(matrix.allocations);
      setOriginalAllocations(matrix.allocations);
      setChangedAllocations([]);
      setLocalAssetClasses(matrix.assetClasses);
      setDeletedAssetClasses([]);
    }
  }, [matrix]);

  // Sorted asset classes for rendering
  const sortedAssetClasses = useMemo(
    () =>
      [...localAssetClasses].sort(
        (a, b) =>
          a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
      ),
    [localAssetClasses],
  );

  // A link from a security's page sets `?assetClass=`, once for each asset
  // class of the security (APP-1486). The matrix is edited and saved whole, so
  // the rows are marked and the first is scrolled to rather than the other rows
  // hidden.
  const [searchParams] = useSearchParams();
  const linkedAssetClasses = useMemo(
    () => new Set(searchParams.getAll("assetClass")),
    [searchParams],
  );
  const firstLinkedAssetClass = sortedAssetClasses.find((ac) =>
    linkedAssetClasses.has(ac.name),
  )?.name;
  const firstLinkedRow = useRef<HTMLTableRowElement | null>(null);
  const scrolledToLinkedRow = useRef(false);
  useEffect(() => {
    if (!scrolledToLinkedRow.current && firstLinkedRow.current) {
      scrolledToLinkedRow.current = true;
      firstLinkedRow.current.scrollIntoView?.({ block: "center" });
    }
  }, [firstLinkedAssetClass]);

  // Handle adding a new asset class
  const handleAddAssetClass = useCallback(() => {
    if (!newAssetClassName.trim() || !matrix) return;

    const trimmedName = newAssetClassName.trim();

    // Check if asset class already exists
    if (localAssetClasses.some((ac) => ac.name === trimmedName)) {
      notificationContext.pushNotification({
        id: "firm-risk-asset-class-add",
        header: "Duplicate Asset Class",
        body: `Asset class "${trimmedName}" already exists`,
        variant: "warning",
      });
      return;
    }

    // Assign next display order
    const maxOrder = localAssetClasses.reduce(
      (max, ac) => Math.max(max, ac.displayOrder),
      0,
    );

    // Add to local asset classes
    setLocalAssetClasses([
      ...localAssetClasses,
      { name: trimmedName, displayOrder: maxOrder + 1 },
    ]);

    // Initialize allocations for the new asset class (all zeros)
    const newAllocations: Record<string, number> = {};
    matrix.riskProfiles.forEach((profile) => {
      newAllocations[profile.name] = 0;
    });
    setAllocations({
      ...allocations,
      [trimmedName]: newAllocations,
    });

    // Clear input
    setNewAssetClassName("");
  }, [
    newAssetClassName,
    matrix,
    localAssetClasses,
    allocations,
    notificationContext,
  ]);

  // Handle deleting an asset class
  const handleDeleteAssetClass = useCallback(
    (assetClass: string) => {
      // Check if it's a new asset class (not yet saved)
      const isNewAssetClass = !matrix?.assetClasses.some(
        (ac) => ac.name === assetClass,
      );

      // Remove from local asset classes
      setLocalAssetClasses(
        localAssetClasses.filter((ac) => ac.name !== assetClass),
      );

      // Remove from allocations
      const newAllocations = { ...allocations };
      delete newAllocations[assetClass];
      setAllocations(newAllocations);

      // Remove any pending changes for this asset class
      setChangedAllocations(
        changedAllocations.filter((c) => c.assetClass !== assetClass),
      );

      // If it's an existing asset class, mark it for deletion
      if (!isNewAssetClass) {
        setDeletedAssetClasses([...deletedAssetClasses, assetClass]);
      }
    },
    [
      matrix,
      localAssetClasses,
      allocations,
      changedAllocations,
      deletedAssetClasses,
    ],
  );

  // Move asset class up or down in display order
  const handleMoveAssetClass = useCallback(
    (assetClassName: string, direction: "up" | "down") => {
      const idx = sortedAssetClasses.findIndex(
        (ac) => ac.name === assetClassName,
      );
      if (idx < 0) return;
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= sortedAssetClasses.length) return;

      // Swap display orders between the two items
      const current = sortedAssetClasses[idx];
      const neighbor = sortedAssetClasses[swapIdx];
      setLocalAssetClasses((prev) =>
        prev.map((ac) => {
          if (ac.name === current.name)
            return { ...ac, displayOrder: neighbor.displayOrder };
          if (ac.name === neighbor.name)
            return { ...ac, displayOrder: current.displayOrder };
          return ac;
        }),
      );
    },
    [sortedAssetClasses],
  );

  // Calculate row totals
  const getRowTotal = useCallback(
    (assetClass: string): number => {
      if (!matrix || !allocations[assetClass]) return 0;
      return matrix.riskProfiles.reduce((sum, profile) => {
        return sum + (allocations[assetClass]?.[profile.name] ?? 0);
      }, 0);
    },
    [allocations, matrix],
  );

  // Check if a row is valid (sums to 100)
  const isRowValid = useCallback(
    (assetClass: string): boolean => {
      const total = getRowTotal(assetClass);
      return Math.abs(total - 100) < 0.01;
    },
    [getRowTotal],
  );

  // Handle allocation change
  const handleAllocationChange = useCallback(
    (
      assetClass: string,
      riskName: string,
      value: number,
      firmRiskId: number,
    ) => {
      const newAllocations = {
        ...allocations,
        [assetClass]: {
          ...allocations[assetClass],
          [riskName]: value,
        },
      };
      setAllocations(newAllocations);

      // Track change
      const existingChangeIndex = changedAllocations.findIndex(
        (c) => c.assetClass === assetClass && c.firmRiskId === firmRiskId,
      );

      const newChange: AllocationChange = {
        firmRiskId,
        assetClass,
        allocationPercent: value,
      };

      if (existingChangeIndex >= 0) {
        const newChanges = [...changedAllocations];
        // Check if we're reverting to original
        if (originalAllocations[assetClass]?.[riskName] === value) {
          newChanges.splice(existingChangeIndex, 1);
        } else {
          newChanges[existingChangeIndex] = newChange;
        }
        setChangedAllocations(newChanges);
      } else if (originalAllocations[assetClass]?.[riskName] !== value) {
        setChangedAllocations([...changedAllocations, newChange]);
      }
    },
    [allocations, changedAllocations, originalAllocations],
  );

  // Discard changes
  const handleDiscardChanges = useCallback(() => {
    if (matrix) {
      setAllocations(matrix.allocations);
      setOriginalAllocations(matrix.allocations);
      setChangedAllocations([]);
      setLocalAssetClasses(matrix.assetClasses);
      setNewAssetClassName("");
      setDeletedAssetClasses([]);
    }
  }, [matrix]);

  // Delete asset class API call
  const deleteAssetClass = useCallback(
    async (assetClass: string) => {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/v1/firm/risk/asset-class/${encodeURIComponent(assetClass)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) throw new Error("Failed to delete asset class");
      return response.json();
    },
    [getAccessTokenSilently],
  );

  // Check if display orders have changed
  const hasDisplayOrderChanges = useMemo(() => {
    if (!matrix) return false;
    return localAssetClasses.some((local) => {
      const original = matrix.assetClasses.find((ac) => ac.name === local.name);
      return original && original.displayOrder !== local.displayOrder;
    });
  }, [matrix, localAssetClasses]);

  // Build display order payload for save
  const displayOrderPayload = useMemo(() => {
    if (!matrix) return undefined;
    const changed: { assetClass: string; displayOrder: number }[] = [];
    for (const local of localAssetClasses) {
      const original = matrix.assetClasses.find((ac) => ac.name === local.name);
      if (!original || original.displayOrder !== local.displayOrder) {
        changed.push({
          assetClass: local.name,
          displayOrder: local.displayOrder,
        });
      }
    }
    return changed.length > 0 ? changed : undefined;
  }, [matrix, localAssetClasses]);

  // Submit mutation.
  //
  // Published at `/v1` by APP-1563, in the same change that bounded its echo —
  // the named change `docs/adr/firm-domain-admission.md` records against it on
  // criterion C8. Before that version landed, `/v1/firm/risk/asset-class`
  // matched `PUT /v1/firm/risk/:id` with `id = "asset-class"` and was refused
  // by `ParseIntPipe` with a 400.
  //
  // The echo is now a PAGE of the firm's allocations, which is what
  // `pagination` beside `data` reports. This screen does not read either: it
  // refetches the fixed matrix at `/v1/firm/risk/asset-class` instead, which is
  // not paginated and still carries every asset class and every risk level. The
  // response type is stated in full anyway so a later reader cannot take `data`
  // here for the whole collection.
  const updateAllocations = useAuthenticatedMutationAsync<{
    data: AllocationChange[];
    pagination?: LimitOffsetPaginationResponse;
  }>(
    "/v1/firm/risk/asset-class",
    async (data: {
      allocations: AllocationChange[];
      displayOrders?: { assetClass: string; displayOrder: number }[];
    }) => ({
      method: "PUT",
      body: JSON.stringify(data),
    }),
  );

  const onSubmit = useMutation({
    mutationFn: async () => {
      // Validate all rows sum to 100
      const invalidRows = sortedAssetClasses
        .map((ac) => ac.name)
        .filter((name) => !isRowValid(name));
      if (invalidRows && invalidRows.length > 0) {
        notificationContext.pushNotification({
          id: "firm-risk-asset-class",
          header: "Validation Error",
          body: `The following asset classes do not sum to 100%: ${invalidRows.join(", ")}`,
          variant: "danger",
        });
        return;
      }

      try {
        // First, delete any marked asset classes
        for (const assetClass of deletedAssetClasses) {
          await deleteAssetClass(assetClass);
        }

        // Then update/create allocations and display orders
        if (changedAllocations.length > 0 || displayOrderPayload) {
          await updateAllocations({
            allocations: changedAllocations,
            displayOrders: displayOrderPayload,
          });
        }

        // The MATRIX read's key, unchanged by APP-1563 and deliberately so:
        // `GET /v1/firm/risk/asset-class` returns a fixed object rather than a
        // list, passes criterion C8, is not paginated, and was versioned by an
        // earlier job. Only the `PUT` on the same path moved in this change,
        // and a mutation's url is not a query key.
        queryClient.invalidateQueries({
          queryKey: ["/v1/firm/risk/asset-class"],
        });
        setChangedAllocations([]);
        setDeletedAssetClasses([]);
        notificationContext.pushNotification({
          id: "firm-risk-asset-class",
          header: "Risk Allocations Updated",
          body: "Risk asset class allocations updated successfully",
          variant: "success",
        });
      } catch (err) {
        console.error("Failed to save risk allocations", err);
        notificationContext.pushNotification({
          id: "firm-risk-asset-class",
          header: "Failed to Save",
          body: "Risk asset class allocations not saved",
          variant: "danger",
        });
      }
    },
  });

  const hasNewAssetClasses =
    matrix && localAssetClasses.length !== matrix.assetClasses.length;
  const hasDeletedAssetClasses = deletedAssetClasses.length > 0;
  const hasChanges =
    changedAllocations.length > 0 ||
    hasNewAssetClasses ||
    hasDeletedAssetClasses ||
    hasDisplayOrderChanges;
  const isAdmin = roles.includes("Admin");

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {isPending ? (
        <Loading />
      ) : isError ? (
        <Alert variant="danger">
          Failed to retrieve risk asset class allocations
        </Alert>
      ) : !matrix || matrix.riskProfiles.length === 0 ? (
        <Alert variant="info">
          <Alert.Heading>No Risk Levels Found</Alert.Heading>
          <p>
            You need to create risk levels before you can configure asset class
            allocations.
          </p>
          <Link to="/firm/risk" className="btn btn-primary">
            Manage Risk Levels
          </Link>
        </Alert>
      ) : (
        <>
          <Row className="mb-3">
            <Col>
              <h4>Risk Asset Class Allocations</h4>
              <p className="text-muted">
                Configure how each asset class is allocated between risk levels.
                Each row must sum to 100%.
              </p>
            </Col>
          </Row>

          {isAdmin && (
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="New asset class name"
                    value={newAssetClassName}
                    onChange={(e) => setNewAssetClassName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddAssetClass();
                      }
                    }}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={handleAddAssetClass}
                    disabled={!newAssetClassName.trim()}
                  >
                    Add Asset Class
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          )}

          {isAdmin && hasChanges && (
            <Row className="mb-2">
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="success"
                  onClick={() => onSubmit.mutateAsync()}
                  disabled={onSubmit.isPending}
                >
                  Submit{" "}
                  {onSubmit.isPending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    ""
                  )}
                </Button>
              </Col>
              <Col className="d-grid gap-1" xl={6} md={12}>
                <Button
                  className="mb-1"
                  variant="danger"
                  onClick={handleDiscardChanges}
                  disabled={onSubmit.isPending}
                >
                  Discard
                </Button>
              </Col>
            </Row>
          )}

          <Row>
            <Col
              style={{ pointerEvents: onSubmit.isPending ? "none" : "auto" }}
            >
              <BsTable striped bordered hover responsive>
                <thead>
                  <tr>
                    {isAdmin && <th className="text-center">Order</th>}
                    <th>Asset Class</th>
                    {matrix.riskProfiles.map((profile) => (
                      <th key={profile.firmRiskId} className="text-center">
                        {profile.name}
                      </th>
                    ))}
                    <th className="text-center">Total</th>
                    {isAdmin && <th className="text-center">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedAssetClasses.map((ac, idx) => {
                    const assetClass = ac.name;
                    const total = getRowTotal(assetClass);
                    const valid = isRowValid(assetClass);
                    const linked = linkedAssetClasses.has(assetClass);

                    return (
                      <tr
                        key={assetClass}
                        ref={
                          assetClass === firstLinkedAssetClass
                            ? firstLinkedRow
                            : undefined
                        }
                        className={linked ? "table-primary" : undefined}
                        aria-current={linked ? "true" : undefined}
                      >
                        {isAdmin && (
                          <td className="text-center text-nowrap">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={idx === 0}
                              onClick={() =>
                                handleMoveAssetClass(assetClass, "up")
                              }
                              title="Move up"
                              className="py-0 px-1"
                            >
                              &#9650;
                            </Button>{" "}
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={idx === sortedAssetClasses.length - 1}
                              onClick={() =>
                                handleMoveAssetClass(assetClass, "down")
                              }
                              title="Move down"
                              className="py-0 px-1"
                            >
                              &#9660;
                            </Button>
                          </td>
                        )}
                        <td>{assetClass}</td>
                        {matrix.riskProfiles.map((profile) => (
                          <td
                            key={`${assetClass}-${profile.firmRiskId}`}
                            className="text-center"
                          >
                            {isAdmin ? (
                              <InputGroup
                                style={{ width: "120px", margin: "auto" }}
                              >
                                <Form.Control
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="1"
                                  value={
                                    allocations[assetClass]?.[profile.name] ?? 0
                                  }
                                  onChange={(e) =>
                                    handleAllocationChange(
                                      assetClass,
                                      profile.name,
                                      parseFloat(e.target.value) || 0,
                                      profile.firmRiskId,
                                    )
                                  }
                                  className={
                                    allocations[assetClass]?.[profile.name] !==
                                    originalAllocations[assetClass]?.[
                                      profile.name
                                    ]
                                      ? "border-warning"
                                      : ""
                                  }
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </InputGroup>
                            ) : (
                              <span>
                                {allocations[assetClass]?.[profile.name] ?? 0}%
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="text-center">
                          <Badge bg={valid ? "success" : "danger"}>
                            {total.toFixed(0)}%
                          </Badge>
                        </td>
                        {isAdmin && (
                          <td className="text-center">
                            <Button
                              size="sm"
                              onClick={() => handleDeleteAssetClass(assetClass)}
                              title="Delete asset class"
                            >
                              <img
                                src="/icons/trash.svg"
                                alt="Delete"
                                height="18"
                                width="18"
                                className="svg-light"
                              />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </BsTable>
            </Col>
          </Row>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default FirmRiskAssetClass;
